import { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min?url";
import { Highlight, PsaFieldResult } from "./types";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

type Props = {
  pdfUrl?: string;
  activeField?: PsaFieldResult | null;
  pdfData?: string;
  highlights?: Highlight[];
  onLoadStart?: () => void;
  onLoadComplete?: () => void;
  onLoadError?: () => void;
  centerActiveHighlight?: boolean;
};

const emptyHighlights: Highlight[] = [];
const pdfJsAssets = {
  cMapPacked: true,
  cMapUrl: "/pdfjs/cmaps/",
  iccUrl: "/pdfjs/iccs/",
  standardFontDataUrl: "/pdfjs/standard_fonts/",
  useWasm: true,
  wasmUrl: "/pdfjs/wasm/"
};

const expandDocumentHighlightBox = (box: HTMLDivElement, horizontalPadding = 8, verticalPadding = 5) => {
  box.style.transform = `translate(-${horizontalPadding}px, -${verticalPadding}px)`;
  box.style.width = `calc(${box.style.width} + ${horizontalPadding * 2}px)`;
  box.style.height = `calc(${box.style.height} + ${verticalPadding * 2}px)`;
};

const applyDocumentHighlightStyle = (box: HTMLDivElement) => {
  Object.assign(box.style, {
    background: "rgba(255, 245, 0, 0.18)",
    border: "3px solid #ff1744",
    borderRadius: "10px",
    boxShadow: "0 0 0 5px rgba(255, 23, 68, 0.18), 0 0 18px rgba(255, 23, 68, 0.55)",
    boxSizing: "border-box",
    outline: "2px solid rgba(255, 255, 255, 0.9)",
    outlineOffset: "-7px"
  });
};

const base64ToUint8Array = (base64: string) => {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return bytes;
};

export default function PdfViewerWithLocation({
  pdfUrl,
  activeField = null,
  pdfData,
  highlights = emptyHighlights,
  onLoadStart,
  onLoadComplete,
  onLoadError,
  centerActiveHighlight = false
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const overlayRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [renderVersion, setRenderVersion] = useState(0);

  const activeHighlights = useMemo(() => activeField?.highlights ?? [], [activeField]);
  const legacyHighlights = useMemo(() => highlights, [highlights]);
  const scrollRenderVersion = centerActiveHighlight ? renderVersion : 0;

  useEffect(() => {
    let cancelled = false;

    const renderPdf = async () => {
      if (!pdfUrl && !pdfData) {
        onLoadComplete?.();
        return;
      }

      onLoadStart?.();
      const documentSource = pdfUrl
        ? { ...pdfJsAssets, url: pdfUrl }
        : { ...pdfJsAssets, data: base64ToUint8Array(pdfData as string) };
      try {
        const pdf = await pdfjsLib.getDocument(documentSource).promise;
        const container = containerRef.current;
        if (!container || cancelled) return;

        container.innerHTML = "";
        pageRefs.current = {};
        overlayRefs.current = {};

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (cancelled) {
            return;
          }

          const page = await pdf.getPage(pageNum);
          const scale = 1.25;
          const viewport = page.getViewport({ scale });

          const wrapper = document.createElement("div");
          wrapper.style.position = "relative";
          wrapper.style.marginBottom = "1rem";
          wrapper.style.width = `${viewport.width}px`;
          wrapper.style.height = `${viewport.height}px`;
          wrapper.style.backgroundColor = "#fff";
          wrapper.style.boxShadow = "0 6px 20px rgba(15, 23, 42, 0.08)";
          wrapper.style.borderRadius = "12px";
          wrapper.style.overflow = "hidden";

          pageRefs.current[pageNum] = wrapper;

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          if (!context) {
            continue;
          }

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.display = "block";

          wrapper.appendChild(canvas);
          container.appendChild(wrapper);

          await page.render({
            canvas,
            canvasContext: context,
            viewport
          }).promise;

          const overlay = document.createElement("div");
          overlay.style.position = "absolute";
          overlay.style.inset = "0";
          overlay.style.pointerEvents = "none";
          overlayRefs.current[pageNum] = overlay;

          wrapper.appendChild(overlay);
        }

        if (!cancelled) {
          setRenderVersion((current) => current + 1);
          onLoadComplete?.();
        }
      } catch {
        if (!cancelled) {
          onLoadError?.();
        }
      }
    };

    renderPdf();

    return () => {
      cancelled = true;
    };
  }, [onLoadComplete, onLoadError, onLoadStart, pdfUrl, pdfData]);

  useEffect(() => {
    Object.values(overlayRefs.current).forEach((overlay) => {
      if (overlay) {
        overlay.innerHTML = "";
      }
    });

    activeHighlights.forEach((highlight, index) => {
      const overlay = overlayRefs.current[highlight.pageNumber];
      if (!overlay) {
        return;
      }

      const box = document.createElement("div");
      box.style.position = "absolute";
      box.style.left = `${highlight.left * 100}%`;
      box.style.top = `${highlight.top * 100}%`;
      box.style.width = `${highlight.width * 100}%`;
      box.style.height = `${highlight.height * 100}%`;
      expandDocumentHighlightBox(box);
      applyDocumentHighlightStyle(box);
      box.id = `highlight-${highlight.pageNumber}-${index}`;
      overlay.appendChild(box);
    });

    legacyHighlights.forEach((highlight, index) => {
      const overlay = overlayRefs.current[highlight.page];
      if (!overlay) {
        return;
      }

      const [topLeft, topRight, , bottomLeft] = highlight.boundingBox;
      const box = document.createElement("div");
      box.style.position = "absolute";
      box.style.left = `${topLeft.x * 100}%`;
      box.style.top = `${topLeft.y * 100}%`;
      box.style.width = `${(topRight.x - topLeft.x) * 100}%`;
      box.style.height = `${(bottomLeft.y - topLeft.y) * 100}%`;
      expandDocumentHighlightBox(box);
      applyDocumentHighlightStyle(box);
      box.id = `legacy-highlight-${highlight.page}-${index}`;
      overlay.appendChild(box);
    });
  }, [activeHighlights, legacyHighlights]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (activeField && activeHighlights.length > 0) {
      const firstHighlight = activeHighlights[0];
      const pageElement = pageRefs.current[firstHighlight.pageNumber];
      if (!pageElement) {
        return;
      }

      const pageTop = pageElement.offsetTop;
      const pageHeight = pageElement.clientHeight;
      const targetOffset = centerActiveHighlight
        ? pageTop + ((firstHighlight.top + (firstHighlight.height / 2)) * pageHeight) - (container.clientHeight / 2)
        : pageTop + (firstHighlight.top * pageHeight) - Math.max(pageHeight * 0.15, 96);

      container.scrollTo({
        top: Math.max(targetOffset, 0),
        ...(centerActiveHighlight ? {
          left: Math.max(
            pageElement.offsetLeft + ((firstHighlight.left + (firstHighlight.width / 2)) * pageElement.clientWidth) - (container.clientWidth / 2),
            0
          )
        } : {}),
        behavior: "smooth"
      });
      return;
    }

    if (activeField?.pageNumber) {
      const pageElement = pageRefs.current[activeField.pageNumber];
      if (!pageElement) {
        return;
      }

      container.scrollTo({
        top: Math.max(pageElement.offsetTop - 48, 0),
        ...(centerActiveHighlight ? { left: Math.max(pageElement.offsetLeft - 48, 0) } : {}),
        behavior: "smooth"
      });
      return;
    }

    if (legacyHighlights.length === 0) {
      return;
    }

    const firstHighlight = legacyHighlights[0];
    const pageElement = pageRefs.current[firstHighlight.page];
    if (!pageElement) {
      return;
    }

    const pageTop = pageElement.offsetTop;
    const pageHeight = pageElement.clientHeight;
    const [topLeft, topRight, , bottomLeft] = firstHighlight.boundingBox;
    const targetOffset = centerActiveHighlight
      ? pageTop + ((topLeft.y + ((bottomLeft.y - topLeft.y) / 2)) * pageHeight) - (container.clientHeight / 2)
      : pageTop + (topLeft.y * pageHeight) - 48;

    container.scrollTo({
      top: Math.max(targetOffset, 0),
      ...(centerActiveHighlight ? {
        left: Math.max(
          pageElement.offsetLeft + ((topLeft.x + ((topRight.x - topLeft.x) / 2)) * pageElement.clientWidth) - (container.clientWidth / 2),
          0
        )
      } : {}),
      behavior: "smooth"
    });
  }, [activeField, activeHighlights, centerActiveHighlight, legacyHighlights, scrollRenderVersion]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "calc(100vh - 96px)",
        overflowY: "auto",
        overflowX: "auto",
        padding: "8px",
        borderRadius: "16px",
        backgroundColor: "#eef2ff"
      }}
    />
  );
}
